import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { createTest, fetchTestById } from "../pages/services/apiService";
import {
  fetchAllSubjects,
  fetchModulesBySubject,
  fetchTopicsByModule,
} from "../pages/services/noteHierarchyService";
import { fetchSections } from "../pages/services/notesService";
import RichTextEditor from "./ReusableComponent/RichTextEditor";
import { fromDateTimeLocal, toDateTimeLocal } from "./utils/utility";
import { fetchBatchOptions } from "../pages/services/filterOptionsService";
import MultiSelectDropdown from "./formComponents/MultiSelectDropdown";
import "../styles/AddForm.css";
import "../styles/AddTests.css";

const Field = ({ label, required, error, children }) => (
  <div className="af-field">
    <label className="af-label">{label}{required && <span className="af-required">*</span>}</label>
    {children}
    {error && <span className="af-error">{error}</span>}
  </div>
);

const SelectField = ({ label, required, error, name, refProp, options, placeholder, value, onChange, disabled }) => (
  <Field label={label} required={required} error={error}>
    <select name={name} ref={refProp} value={value} onChange={onChange} disabled={disabled}
      className={error ? "error-input" : ""}>
      <option value="">{placeholder}</option>
      {options.map((o, i) =>
        typeof o === "string"
          ? <option key={i} value={o}>{o}</option>
          : <option key={o.value} value={o.value}>{o.label}</option>
      )}
    </select>
  </Field>
);

const AddTests = () => {
  const testTitleRef         = useRef(null);
  const [selectedBatches, setSelectedBatches] = useState([]);
  const testTopicRef         = useRef(null);
  const testDurationRef      = useRef(null);
  const testStartFromRef     = useRef(null);
  const testStartToRef       = useRef(null);
  const testEndTimeRef       = useRef(null);
  const testSectionRef       = useRef(null);
  const analysisStartFromRef = useRef(null);
  const analysisStartToRef   = useRef(null);
  const analysisEndTimeRef   = useRef(null);
  const analysisDurationRef  = useRef(null);
  const isAnalysisRequiredRef = useRef(null);
  const protectiveTestRef    = useRef(null);
  const isFullScreenRef      = useRef(null);
  const testTypeRef          = useRef(null);
  const requiredTimerRef     = useRef(null);
  const testStatusRef        = useRef(null);

  const [formData, setFormData] = useState({
    testTitle: "", batchIds: [], testDuration: "", testDescription: "",
    testInstructions: "", testSubject: "", testTopic: "", testStartFrom: "",
    testStartTo: "", testEndTime: "", testModule: "", testSection: "",
    analysisStartFrom: "", analysisStartTo: "", analysisEndTime: "",
    analysisDuration: "", isAnalysisRequired: "", protectiveTest: "",
    isFullScreen: "", testType: "", requiredTimer: "", testStatus: "",
  });

  const [formErrors, setFormErrors] = useState({});
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [showAnalysis, setShowAnalysis] = useState(false);
  const [subjects, setSubjects] = useState([]);
  const [modules, setModules]   = useState([]);
  const [sections, setSections] = useState([]);
  const [topics, setTopics]     = useState([]);
  const [batches, setBatches]   = useState([]);
  // Subject and module are multi-select; each holds react-select {label,value}
  // objects. Topic and section stay single and are optional.
  const [copiedBatchIds, setCopiedBatchIds] = useState([]);
  const [copiedFromTitle, setCopiedFromTitle] = useState("");
  const [selectedSubjects, setSelectedSubjects] = useState([]);
  const [selectedModules, setSelectedModules]   = useState([]);
  const [protectiveChecked, setProtectiveChecked] = useState(true);
  const [fullScreenChecked, setFullScreenChecked] = useState(true);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const copyFromId = searchParams.get("copyFrom");

  // Every input below shows formData and writes back to it — the refs are only
  // read on submit. (They used to be the inputs' only source, so a prefill set
  // in formData never reached the screen: a copied test came up blank.)
  const bind = (name) => ({
    value: formData[name] ?? "",
    onChange: (e) => setFormData((p) => ({ ...p, [name]: e.target.value })),
  });

  // "Copy to new test": every detail of the source test is carried over —
  // title, batches, classification, schedule, analysis, status, settings,
  // description and instructions — except its questions. The new test starts
  // with none; they are added from its own page once it is created.
  useEffect(() => {
    if (!copyFromId) return;
    let cancelled = false;
    fetchTestById(copyFromId)
      .then(async (src) => {
        if (cancelled || !src) return;
        const asList = (v) => (Array.isArray(v) ? v : v && v._id ? [v] : []);
        const yesNo = (v) => (v === true || v === "true" ? "true" : v === false || v === "false" ? "false" : "");
        const topicId = src.testTopic?._id || src.testTopic || "";
        setFormData((prev) => ({
          ...prev,
          testTitle: src.testTitle || "",
          testDescription: src.testDescription || "",
          testInstructions: src.testInstructions || "",
          testTopic: topicId,
          testSection: src.testSection?._id || src.testSection || "",
          testDuration: src.testDuration || "",
          testStartFrom: toDateTimeLocal(src.testStartFrom) || "",
          testStartTo: toDateTimeLocal(src.testStartTo) || "",
          testEndTime: toDateTimeLocal(src.testEndTime) || "",
          isAnalysisRequired: yesNo(src.isAnalysisRequired),
          analysisStartFrom: toDateTimeLocal(src.analysisStartFrom) || "",
          analysisStartTo: toDateTimeLocal(src.analysisStartTo) || "",
          analysisEndTime: toDateTimeLocal(src.analysisEndTime) || "",
          analysisDuration: src.analysisDuration || "",
          testType: src.testType || "",
          testStatus: src.testStatus || "",
          requiredTimer: yesNo(src.requiredTimer),
        }));
        setShowAnalysis(yesNo(src.isAnalysisRequired) === "true");
        setProtectiveChecked(!!src.protectiveTest);
        setFullScreenChecked(!!src.isFullScreen);
        setCopiedFromTitle(src.testTitle || "Untitled test");
        const ids = Array.isArray(src.batchIds) && src.batchIds.length
          ? src.batchIds.map(String)
          : (src.batch?._id ? [String(src.batch._id)] : []);
        setCopiedBatchIds(ids);
        setSelectedSubjects(asList(src.testSubject).map((x) => ({ value: String(x._id || x), label: x.subject || x.title || "" })));
        setSelectedModules(asList(src.testModule).map((x) => ({ value: String(x._id || x), label: x.module || x.title || "" })));
        // The section list is otherwise only loaded when a topic is picked.
        if (topicId) {
          const secs = await fetchSections(topicId).catch(() => []);
          if (!cancelled) setSections((secs || []).map((x) => ({ value: x._id, label: x.title })));
        }
      })
      .catch((e) => console.error("Could not load the test to copy:", e));
    return () => { cancelled = true; };
  }, [copyFromId]);

  // Batch labels only arrive with the options, so resolve once both are in.
  useEffect(() => {
    if (!copiedBatchIds.length || !batches.length) return;
    setSelectedBatches(
      copiedBatchIds.map((id) => batches.find((b) => String(b.value) === String(id))).filter(Boolean)
    );
  }, [copiedBatchIds, batches]);

  useEffect(() => {
    // Shared source: active-only, and not truncated at 10 the way
    // fetchBatches() was (it sent `limit`, which the API ignores).
    fetchBatchOptions().then(opts => {
      const res = { data: opts.map(o => ({ _id: o.value, batchTitle: o.label })) };
      setBatches(res.data.map(b => ({ value: b._id, label: b.batchTitle })));
    }).catch(console.error);
    fetchAllSubjects().then(setSubjects).catch(console.error);
  }, []);

  // Modules are the union across every chosen subject, topics the union across
  // every chosen module — so widening the selection widens what is offered next
  // rather than resetting it.
  useEffect(() => {
    const ids = selectedSubjects.map(s => s.value);
    if (!ids.length) { setModules([]); setSelectedModules([]); setTopics([]); setSections([]); return; }
    Promise.all(ids.map(id => fetchModulesBySubject(id).catch(() => [])))
      .then(lists => {
        const merged = [];
        const seen = new Set();
        lists.flat().forEach(m => { if (!seen.has(m.value)) { seen.add(m.value); merged.push(m); } });
        setModules(merged);
        // Drop any module that no longer belongs to the remaining subjects
        setSelectedModules(prev => prev.filter(m => seen.has(m.value)));
      })
      .catch(() => setModules([]));
  }, [selectedSubjects]);

  useEffect(() => {
    const ids = selectedModules.map(m => m.value);
    if (!ids.length) { setTopics([]); setSections([]); return; }
    Promise.all(ids.map(id => fetchTopicsByModule(id).catch(() => [])))
      .then(lists => {
        const merged = [];
        const seen = new Set();
        lists.flat().forEach(t => { if (!seen.has(t.value)) { seen.add(t.value); merged.push(t); } });
        setTopics(merged);
      })
      .catch(() => setTopics([]));
  }, [selectedModules]);

  const handleTopicChange = async (topicId) => {
    setFormData(p => ({ ...p, testTopic: topicId, testSection: "" }));
    setSections([]);
    if (topicId) {
      try {
        const secs = await fetchSections(topicId);
        setSections((secs || []).map(s => ({ value: s._id, label: s.title })));
      } catch { setSections([]); }
    }
  };

  const validate = () => {
    const e = {};
    if (!testTitleRef.current?.value?.trim())         e.testTitle         = "Test title is required";
    if (!selectedBatches.length)                      e.batchIds          = "At least one batch is required";
    // Subject is the only classification a test must have — module, topic and
    // section narrow it down but a subject-wide test needs none of them.
    if (!selectedSubjects.length)                     e.testSubject       = "Select at least one subject";
    if (!testStartFromRef.current?.value?.trim())     e.testStartFrom     = "Start from is required";
    if (!testStartToRef.current?.value?.trim())       e.testStartTo       = "Start to is required";
    if (!testEndTimeRef.current?.value?.trim())       e.testEndTime       = "End time is required";
    if (!testDurationRef.current?.value?.trim())      e.testDuration      = "Duration is required";
    if (!isAnalysisRequiredRef.current?.value?.trim()) e.isAnalysisRequired = "Required";
    if (!testStatusRef.current?.value?.trim())        e.testStatus        = "Status is required";
    if (!testTypeRef.current?.value?.trim())          e.testType          = "Type is required";
    if (!requiredTimerRef.current?.value?.trim())     e.requiredTimer     = "Required";
    if (!formData.testDescription?.replace(/<[^>]*>/g, "").trim()) e.testDescription = "Description is required";
    if (!formData.testInstructions?.replace(/<[^>]*>/g, "").trim()) e.testInstructions = "Instructions are required";
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const ve = validate();
    if (Object.keys(ve).length > 0) {
      setFormErrors(ve);
      setErrorMessage("Please correct the errors below.");
      return;
    }
    setLoading(true);
    try {
      let payload = {
        testInstructions: formData.testInstructions,
        testDescription: formData.testDescription,
        testTitle: testTitleRef.current.value,
        // batchIds is the source of truth; the model derives the primary batch.
        batchIds: selectedBatches.map(b => b.value),
        testSubject: selectedSubjects.map(s => s.value),
        testModule: selectedModules.map(m => m.value),
        testTopic: formData.testTopic || null,
        testSection: formData.testSection || null,
        testStartFrom: fromDateTimeLocal(testStartFromRef.current.value),
        testStartTo: fromDateTimeLocal(testStartToRef.current.value),
        testEndTime: fromDateTimeLocal(testEndTimeRef.current.value),
        testDuration: testDurationRef.current.value,
        isAnalysisRequired: isAnalysisRequiredRef.current.value,
        protectiveTest: protectiveTestRef.current?.checked || false,
        isFullScreen: isFullScreenRef.current?.checked || false,
        testType: testTypeRef.current.value,
        testStatus: testStatusRef.current.value,
        requiredTimer: requiredTimerRef.current.value,
      };
      if (showAnalysis) {
        payload = {
          ...payload,
          analysisStartFrom: fromDateTimeLocal(analysisStartFromRef.current.value),
          analysisStartTo: fromDateTimeLocal(analysisStartToRef.current.value),
          analysisEndTime: fromDateTimeLocal(analysisEndTimeRef.current.value),
          analysisDuration: analysisDurationRef.current.value,
        };
      }
      await createTest(payload);
      sessionStorage.setItem("ToastMessage", "Test created successfully!");
      navigate("/tests");
    } catch (err) {
      setErrorMessage("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
      setFormErrors({});
    }
  };

  return (
    <div className="af-page">
      {loading && (
        <div className="af-overlay">
          <div className="af-spinner" />
          <p className="af-overlay-text">Creating test…</p>
        </div>
      )}

      <div className="af-card">
        <div className="af-header">
          <div className="af-header-icon sky">📋</div>
          <div className="af-header-text">
            <h2 className="af-title">{copyFromId ? "Copy Test" : "Add New Test"}</h2>
            <p className="af-subtitle">
              {copyFromId && copiedFromTitle
                ? `Copied from “${copiedFromTitle}” — every detail except its questions. Add questions after creating the test.`
                : "Configure the test structure, timing, and settings"}
            </p>
          </div>
        </div>

        {errorMessage && <div className="af-alert af-alert-error" style={{margin:"16px 32px 0"}}>{errorMessage}</div>}

        <form onSubmit={handleSubmit}>
          <div className="af-body">

            {/* Basic Info */}
            <div className="af-section">
              <p className="af-section-title">Test Information</p>
              <Field label="Test Title" required error={formErrors.testTitle}>
                <input type="text" name="testTitle" ref={testTitleRef} {...bind("testTitle")}
                  placeholder="e.g. React Mid-Term Test"
                  className={formErrors.testTitle ? "error-input" : ""} />
              </Field>
              <div className="af-row">
                <Field label="Batches" required error={formErrors.batchIds}>
                  <MultiSelectDropdown
                    options={batches}
                    value={selectedBatches}
                    onChange={setSelectedBatches}
                    placeholder="Select one or more batches…"
                    showCheckboxes
                  />
                  <span className="af-hint">
                    The test is offered to every batch selected. The first is its primary batch.
                  </span>
                </Field>
                <SelectField label="Test Type" required error={formErrors.testType}
                  name="testType" refProp={testTypeRef} {...bind("testType")}
                  options={["Practice","Assignment","Daily-test","Weekly-test","Monthly-test"]}
                  placeholder="Select type" />
              </div>
              <Field label="Duration" required error={formErrors.testDuration}>
                <input type="text" name="testDuration" ref={testDurationRef} {...bind("testDuration")}
                  placeholder="e.g. 60 minutes"
                  className={formErrors.testDuration ? "error-input" : ""} />
              </Field>
            </div>

            {/* Subject Classification */}
            <div className="af-section">
              <p className="af-section-title">Subject Classification</p>
              <div className="af-row">
                <Field label="Subject" required error={formErrors.testSubject}>
                  <MultiSelectDropdown
                    options={subjects}
                    value={selectedSubjects}
                    onChange={(vals) => {
                      setSelectedSubjects(vals);
                      // Narrowing the subjects can invalidate the topic/section
                      // below, so clear them rather than leave a stale id.
                      setFormData(p => ({ ...p, testTopic: "", testSection: "" }));
                      setSections([]);
                    }}
                    placeholder={subjects.length === 0 ? "Loading…" : "Select one or more subjects"}
                    showCheckboxes
                  />
                </Field>
                <Field label="Module" error={formErrors.testModule}>
                  <MultiSelectDropdown
                    options={modules}
                    value={selectedModules}
                    onChange={(vals) => {
                      setSelectedModules(vals);
                      setFormData(p => ({ ...p, testTopic: "", testSection: "" }));
                      setSections([]);
                    }}
                    placeholder={!selectedSubjects.length ? "Select subject first" : modules.length === 0 ? "No modules" : "Optional — select modules"}
                    isDisabled={!selectedSubjects.length}
                    showCheckboxes
                  />
                </Field>
              </div>
              <div className="af-row">
                <SelectField label="Topic" error={formErrors.testTopic}
                  name="testTopic" refProp={testTopicRef} options={topics} value={formData.testTopic}
                  placeholder={!selectedModules.length ? "Select module first" : topics.length === 0 ? "No topics" : "Optional — select topic"}
                  disabled={!selectedModules.length}
                  onChange={e => handleTopicChange(e.target.value)} />
                <SelectField label="Section" error={formErrors.testSection}
                  name="testSection" refProp={testSectionRef} options={sections} value={formData.testSection}
                  placeholder={!formData.testTopic ? "Select topic first" : sections.length === 0 ? "No sections" : "Optional — select section"}
                  disabled={!formData.testTopic}
                  onChange={e => setFormData(p => ({ ...p, testSection: e.target.value }))} />
              </div>
            </div>

            {/* Timing */}
            <div className="af-section">
              <p className="af-section-title">Test Window</p>
              <div className="af-row">
                <Field label="Start Window — From" required error={formErrors.testStartFrom}>
                  <input type="datetime-local" name="testStartFrom" ref={testStartFromRef} {...bind("testStartFrom")}
                    className={formErrors.testStartFrom ? "error-input" : ""} />
                </Field>
                <Field label="Start Window — To" required error={formErrors.testStartTo}>
                  <input type="datetime-local" name="testStartTo" ref={testStartToRef} {...bind("testStartTo")}
                    className={formErrors.testStartTo ? "error-input" : ""} />
                </Field>
              </div>
              <Field label="Test End Time" required error={formErrors.testEndTime}>
                <input type="datetime-local" name="testEndTime" ref={testEndTimeRef} {...bind("testEndTime")}
                  className={formErrors.testEndTime ? "error-input" : ""} />
              </Field>
            </div>

            {/* Analysis */}
            <div className="af-section">
              <p className="af-section-title">Analysis Settings</p>
              <SelectField label="Is Analysis Required?" required error={formErrors.isAnalysisRequired}
                name="isAnalysisRequired" refProp={isAnalysisRequiredRef}
                options={["true", "false"]} placeholder="Select…"
                value={formData.isAnalysisRequired}
                onChange={e => {
                  const v = e.target.value;
                  setFormData(p => ({ ...p, isAnalysisRequired: v }));
                  setShowAnalysis(v === "true");
                }} />

              {showAnalysis && (
                <>
                  <div className="af-row">
                    <Field label="Analysis Start — From" error={formErrors.analysisStartFrom}>
                      <input type="datetime-local" name="analysisStartFrom" ref={analysisStartFromRef} {...bind("analysisStartFrom")} />
                    </Field>
                    <Field label="Analysis Start — To" error={formErrors.analysisStartTo}>
                      <input type="datetime-local" name="analysisStartTo" ref={analysisStartToRef} {...bind("analysisStartTo")} />
                    </Field>
                  </div>
                  <div className="af-row">
                    <Field label="Analysis End Time" error={formErrors.analysisEndTime}>
                      <input type="datetime-local" name="analysisEndTime" ref={analysisEndTimeRef} {...bind("analysisEndTime")} />
                    </Field>
                    <Field label="Analysis Duration" error={formErrors.analysisDuration}>
                      <input type="text" name="analysisDuration" ref={analysisDurationRef} {...bind("analysisDuration")}
                        placeholder="e.g. 30 minutes" />
                    </Field>
                  </div>
                </>
              )}
            </div>

            {/* Settings */}
            <div className="af-section">
              <p className="af-section-title">Test Settings</p>
              <div className="af-row">
                <SelectField label="Status" required error={formErrors.testStatus}
                  name="testStatus" refProp={testStatusRef} {...bind("testStatus")}
                  options={["Drafted", "Live", "Completed"]} placeholder="Select status" />
                <SelectField label="Required Timer" required error={formErrors.requiredTimer}
                  name="requiredTimer" refProp={requiredTimerRef} {...bind("requiredTimer")}
                  options={["true", "false"]} placeholder="Select…" />
              </div>

              <div className="af-toggles">
                <div className="af-toggle-row">
                  <span className="af-toggle-label">Protective test (prevent tab switch)</span>
                  <label className="af-toggle">
                    <input type="checkbox" ref={protectiveTestRef}
                      checked={protectiveChecked}
                      onChange={() => setProtectiveChecked(p => !p)} />
                    <span className="af-toggle-track" />
                  </label>
                </div>
                <div className="af-toggle-row">
                  <span className="af-toggle-label">Open test in full screen</span>
                  <label className="af-toggle">
                    <input type="checkbox" ref={isFullScreenRef}
                      checked={fullScreenChecked}
                      onChange={() => setFullScreenChecked(p => !p)} />
                    <span className="af-toggle-track" />
                  </label>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="af-section">
              <p className="af-section-title">Content</p>
              <div className="af-field">
                <label className="af-label">Test Description<span className="af-required">*</span></label>
                <div className="af-editor-wrap">
                  <RichTextEditor
                    value={formData.testDescription}
                    onBlur={v => setFormData(p => ({ ...p, testDescription: v }))}
                    toolbar="full"
                    height={520}
                    placeholder="Describe what this test covers…"
                  />
                </div>
                {formErrors.testDescription && <span className="af-error">{formErrors.testDescription}</span>}
              </div>

              <div className="af-field">
                <label className="af-label">Test Instructions<span className="af-required">*</span></label>
                <div className="af-editor-wrap">
                  <RichTextEditor
                    value={formData.testInstructions}
                    onBlur={v => setFormData(p => ({ ...p, testInstructions: v }))}
                    toolbar="full"
                    height={520}
                    placeholder="Instructions for students…"
                  />
                </div>
                {formErrors.testInstructions && <span className="af-error">{formErrors.testInstructions}</span>}
              </div>
            </div>
          </div>

          <div className="af-submit-row">
            <button type="submit" className="af-submit" disabled={loading}>
              {loading ? "Creating…" : "Create Test"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddTests;
